import {
  Grid,
  Dialog,
  DialogContent,
  Typography,
  makeStyles,
  IconButton,
  Container,
  TextField,
} from '@material-ui/core'
import {
  DataGrid,
  GridCellValue,
  GridColDef,
  GridRowId,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValueFormatterParams,
} from '@material-ui/data-grid'
import { Close } from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import Fuse from 'fuse.js'
import { GetStaticProps } from 'next'
import React, { useState } from 'react'

import Link from '@aletheia/common/components/Link'
import Page from '@aletheia/common/components/Page'

import { funds } from '../../lib/airtable/queries/funds'
import { publicGrantsDatabase } from '../../lib/airtable/queries/publicGrantsDatabase'

// URL to fetch json for
// https://docs.google.com/spreadsheets/d/1s2GC0EjC9aSveKgT2sGMHkzsA6GgHaldjS2FItjQgWE/edit#gid=0
const publicGrantsDatabaseAddonSpreadsheet = `https://sheets.googleapis.com/v4/spreadsheets/1s2GC0EjC9aSveKgT2sGMHkzsA6GgHaldjS2FItjQgWE/values/Data?alt=json&key=AIzaSyCpTVO_rS2z-Hd9Sih2Hqc1K8ha2R8HuXk`

type Grant = {
  id: string
  fund: string
  description: string
  amount: string
  grantee: string
  round: string
  published: boolean
}

type GrantsDatabaseProps = {
  GrantsList: Grant[]
}

const fuseOptions = {
  keys: ['fund', 'description', 'grantee', 'round'],
  threshold: 0.2,
}

const fundSlugs: { [key: string]: string } = {
  'Long-Term Future Fund': 'far-future',
  'EA Infrastructure Fund': 'ea-community',
  'Animal Welfare Fund': 'animal-welfare',
  'Global Health and Development Fund': 'global-development',
}

const columns: GridColDef[] = [
  {
    field: 'fund',
    headerName: 'Fund name',
    width: 200,
  },
  {
    field: 'round',
    headerName: 'Grant round',
    width: 170,
  },
  {
    field: 'description',
    headerName: 'Project description',
    flex: 1,
  },
  {
    field: 'grantee',
    headerName: 'Grantee',
    width: 250,
  },
  {
    field: 'amount',
    headerName: 'Payout amount',
    width: 150,
    type: 'number',
    valueFormatter: (params: GridValueFormatterParams) => {
      const valueFormatted = Number(params.value as number).toLocaleString(
        'en-US',
        {
          style: 'currency',
          currency: 'USD',
        },
      )
      return valueFormatted
    },
    valueParser: (value: GridCellValue) => Number(value),
  },
]

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <Grid container justifyContent="space-between">
        <Grid item>
          <GridToolbarFilterButton />
        </Grid>

        <Grid item>
          <GridToolbarExport />
        </Grid>
      </Grid>
    </GridToolbarContainer>
  )
}

const useStyles = makeStyles((theme) => ({
  dialog: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  gridContainer: {
    padding: theme.spacing(3),
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: theme.palette.grey[500],
    padding: 1,
    '& .MuiSvgIcon-root': {
      top: 1,
    },
  },
}))

const GrantsDatabase: React.FC<GrantsDatabaseProps> = ({ GrantsList }) => {
  const classes = useStyles()

  const [pageSize, setPageSize] = React.useState<number>(20)
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([])
  const [searchText, setSearchText] = useState<string>('')

  const selectedGrant: Grant | undefined = GrantsList.find(
    (Grant: Grant) => Grant.id === selectionModel[0],
  )

  const selectedFundSlug: string = selectedGrant
    ? fundSlugs[selectedGrant.fund]
    : ''

  let rows: Grant[] = []
  if (searchText !== '') {
    const fuse = new Fuse(GrantsList, fuseOptions)
    rows = fuse.search(searchText).map((result) => result.item)
  } else {
    rows = GrantsList
  }

  return (
    <Page title={'Grants Database'} variant="full">
      <Dialog open={!!selectedGrant} onClose={() => setSelectionModel([])}>
        {selectedGrant && (
          <>
            <IconButton
              className={classes.closeButton}
              onClick={() => setSelectionModel([])}
            >
              <Close />
            </IconButton>
            <DialogContent className={classes.dialog}>
              <p>
                <h2>{selectedGrant.grantee}</h2>
                <Typography component="span">
                  {selectedGrant.description}
                </Typography>
              </p>
              <Typography>
                <b>Fund:</b>{' '}
                <Link href={`/funds/${selectedFundSlug}`}>
                  {selectedGrant.fund}
                </Link>
                <br />
                <b>Payout amount:</b>{' '}
                {Number(selectedGrant.amount).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                })}
                <br />
                <b>Grant round:</b> {selectedGrant.round}
              </Typography>
            </DialogContent>
          </>
        )}
      </Dialog>
      <Container>
        <Typography component="div">
          <p>
            This database lists grants we{' '}
            <Link href="/grantmaking-approach">have recommended</Link> in the
            past. <br />
            If you are interested in applying for a grant,{' '}
            <Link href="/apply-for-funding">read this page</Link>.
          </p>
          <p>
            <Alert severity="info">
              Please note that this page is in beta testing and grant data may
              not be accurate
            </Alert>
          </p>
        </Typography>
        <TextField
          label="Search"
          variant="outlined"
          name="search"
          helperText="Full-text search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          fullWidth
        />
      </Container>
      <div
        className={classes.gridContainer}
        style={{ height: (pageSize + 1) * 52 + 160 }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[20, 50, 100]}
          onSelectionModelChange={(newSelection) => {
            setSelectionModel(newSelection)
          }}
          pagination
          selectionModel={selectionModel}
          components={{
            Toolbar: CustomToolbar,
          }}
        />
      </div>
    </Page>
  )
}

export default GrantsDatabase

export const getStaticProps: GetStaticProps = async () => {
  const fundsData = await funds()
  const fundsList = fundsData.reduce<{ [key: string]: string }>(
    (result, item) => {
      const key = item.getId()
      result[key] = item.get('Name') as string
      return result
    },
    {},
  )

  // Get list from Airtable
  // https://airtable.com/appkpJhjwz4ol8ofr/tblemYFkz9RaRdROx/viw3FYmqR2TRsvebu?blocks=hide
  const grantsData = await publicGrantsDatabase()
  const AirtableGrantsList: Grant[] = grantsData.map((grant) => {
    const fund = grant.get('Fund Evaluating') as string

    return {
      id: grant.getId(),
      fund: fundsList[fund],
      description: grant.get('Project short description'),
      grantee: grant.get('Grants Database - Grantee Name'),
      amount: grant.get('Payout Amount'),
      round: (grant.get('Grant round name') as string[])[0],
    } as Grant
  })

  // Get list from spreadsheet
  // https://docs.google.com/spreadsheets/d/1s2GC0EjC9aSveKgT2sGMHkzsA6GgHaldjS2FItjQgWE/edit#gid=0
  const result = await fetch(publicGrantsDatabaseAddonSpreadsheet)
  const data = await result.json()
  const grantsAddonData = data?.values?.slice(1)

  const SpreadsheetGrantsList: Grant[] =
    grantsAddonData &&
    grantsAddonData
      .map((grant: string[], i: number) => {
        return {
          id: `grantsAddonData[${i}]`,
          fund: grant[0],
          description: grant[2],
          grantee: grant[3],
          amount: grant[4].replace(/[^\d.]/gi, ''),
          round: grant[1],
          published: grant[5] === 'TRUE',
        } as Grant
      })
      .filter((grant: Grant) => grant.published)

  // Sort by round name (newest first)
  const GrantsList = [...AirtableGrantsList, ...SpreadsheetGrantsList].sort(
    (a, b) => {
      if (a.round > b.round) return -1
      if (a.round < b.round) return 1
      return 0
    },
  )

  return {
    props: { GrantsList },
    revalidate: 60,
  }
}
